import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchInvoiceSeries } from "services/series.service";

/**
 * The company's sales invoice numbering registers.
 *
 * A separate list from `seriesReducer`: that one holds the shared series used
 * for file naming, contracts and fuel reports, which the Finvalda sync also
 * writes to. These exist only to number sales invoices, and the two must not
 * appear in each other's pickers.
 *
 * No active flag: which series invoices use is a setting on the Orders settings
 * slot, not a property of the series.
 */
export interface InvoiceSeriesEntity {
  id: string;
  title: string;
  /** The `{SERIES}` token printed on the invoice. Frozen once the series issues. */
  label: string;
  description: string | null;
  numberTemplate: string;
  numberTemplateIsDefault: boolean;
  numberStart: number | null;
  currentPeriod: string;
  issuedCount: number;
  numberingLocked: boolean;
  numberPreview: string | null;
}

interface InvoiceSeriesState {
  isLoading: boolean;
  isError: boolean;
  errMsg: string;
  collection: InvoiceSeriesEntity[];
}

const initialState: InvoiceSeriesState = {
  isLoading: true,
  isError: false,
  errMsg: "",
  collection: [],
};

const invoiceSeriesSlice = createSlice({
  name: "invoiceSeries",
  initialState,
  /* eslint-disable no-param-reassign */
  reducers: {
    setDestroy() {
      return initialState;
    },
    setLoading(state: InvoiceSeriesState, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(
      state: InvoiceSeriesState,
      action: PayloadAction<{ error: string }>
    ) {
      state.isLoading = false;
      state.isError = true;
      state.errMsg = action.payload.error;
    },
    setData(
      state: InvoiceSeriesState,
      action: PayloadAction<InvoiceSeriesEntity[]>
    ) {
      state.collection = action.payload;
      state.isLoading = false;
    },
  },
});

const { actions, reducer } = invoiceSeriesSlice;

export const { setDestroy, setLoading, setError, setData } = actions;
export default reducer;

export const fetchInvoiceSeriesList =
  (companyId: string) => async (dispatch: any) => {
    dispatch(setLoading(true));
    try {
      const data = await fetchInvoiceSeries(companyId);
      // The endpoint returns { series: [...] }; anything else means the shape
      // changed under us, and an empty list beats a crashed screen.
      dispatch(setData(Array.isArray(data?.series) ? data.series : []));
    } catch (e) {
      dispatch(setError({ error: "not_fetch" }));
    } finally {
      dispatch(setLoading(false));
    }
  };
